import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Input, List, Modal, Space, Switch, Tag, Typography } from 'antd';
import { useState } from 'react';
import { authApi } from '../auth.api';
import { authQueryKey, useAuth } from '../auth-context';
import type { PublicUser } from '../types';

const sessionsKey = ['auth', 'sessions'] as const;

export function SecuritySettingsPage() {
  const { message } = App.useApp();
  const { user, setUser } = useAuth();
  const queryClient = useQueryClient();
  const [disableOpen, setDisableOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [totpSetup, setTotpSetup] = useState<{ secret: string; keyUri: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const sessionsQuery = useQuery({ queryKey: sessionsKey, queryFn: authApi.listSessions });

  const refreshUser = (updated: PublicUser) => {
    setUser(updated);
    void queryClient.invalidateQueries({ queryKey: authQueryKey });
  };

  const enable2fa = useMutation({
    mutationFn: authApi.enableTwoFactor,
    onSuccess: (u) => {
      refreshUser(u);
      message.success('Two-factor authentication enabled.');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed'),
  });

  const disable2fa = useMutation({
    mutationFn: () => authApi.disableTwoFactor({ password }),
    onSuccess: (u) => {
      refreshUser(u);
      setDisableOpen(false);
      setPassword('');
      message.success('Two-factor authentication disabled.');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed'),
  });

  const beginTotp = useMutation({
    mutationFn: authApi.beginTotpSetup,
    onSuccess: (data) => setTotpSetup(data),
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed'),
  });

  const confirmTotp = useMutation({
    mutationFn: () => authApi.confirmTotpSetup(totpCode),
    onSuccess: (data) => {
      refreshUser(data.user);
      setTotpSetup(null);
      setTotpCode('');
      setBackupCodes(data.backupCodes);
      message.success('Authenticator app enabled.');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Invalid code'),
  });

  const revoke = useMutation({
    mutationFn: authApi.revokeSession,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionsKey });
      message.success('Session revoked.');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Two-factor authentication">
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <Typography.Text>
            Email one-time codes {user?.twoFactorEnabled ? 'are enabled' : 'are disabled'}.
          </Typography.Text>
          <Switch
            checked={user?.twoFactorEnabled ?? false}
            loading={enable2fa.isPending}
            onChange={(checked) => {
              if (checked) enable2fa.mutate();
              else setDisableOpen(true);
            }}
          />
        </Space>
      </Card>

      <Card title="Authenticator app (TOTP)">
        <Typography.Paragraph type="secondary">
          Use an authenticator app (Google Authenticator, Authy, 1Password) for stronger 2FA than
          email codes. You&apos;ll also get one-time backup codes.
        </Typography.Paragraph>
        {!totpSetup ? (
          <Button onClick={() => beginTotp.mutate()} loading={beginTotp.isPending}>
            Set up authenticator app
          </Button>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>
              Add this secret to your authenticator app, then enter the 6-digit code:
            </Typography.Text>
            <Typography.Text code copyable>
              {totpSetup.secret}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all' }}>
              {totpSetup.keyUri}
            </Typography.Text>
            <Space>
              <Input
                placeholder="123456"
                value={totpCode}
                maxLength={6}
                style={{ width: 120 }}
                onChange={(e) => setTotpCode(e.target.value)}
              />
              <Button
                type="primary"
                loading={confirmTotp.isPending}
                disabled={totpCode.length !== 6}
                onClick={() => confirmTotp.mutate()}
              >
                Verify &amp; enable
              </Button>
            </Space>
          </Space>
        )}
      </Card>

      <Card title="Active sessions">
        <List
          loading={sessionsQuery.isLoading}
          dataSource={sessionsQuery.data ?? []}
          locale={{ emptyText: 'No active sessions' }}
          renderItem={(s) => (
            <List.Item
              actions={[
                s.current ? (
                  <Tag color="green" key="current">
                    This device
                  </Tag>
                ) : (
                  <Button
                    key="revoke"
                    danger
                    size="small"
                    loading={revoke.isPending}
                    onClick={() => revoke.mutate(s.id)}
                  >
                    Revoke
                  </Button>
                ),
              ]}
            >
              <List.Item.Meta
                title={s.userAgent ?? 'Unknown device'}
                description={`${s.ip ?? 'unknown IP'} · last used ${new Date(s.lastUsedAt).toLocaleString()}`}
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title="Save your backup codes"
        open={backupCodes !== null}
        footer={[
          <Button key="done" type="primary" onClick={() => setBackupCodes(null)}>
            I&apos;ve saved them
          </Button>,
        ]}
        onCancel={() => setBackupCodes(null)}
      >
        <Typography.Paragraph>
          Store these one-time codes somewhere safe. Each works once if you lose your authenticator.
        </Typography.Paragraph>
        <Space wrap>
          {(backupCodes ?? []).map((c) => (
            <Typography.Text code key={c}>
              {c}
            </Typography.Text>
          ))}
        </Space>
      </Modal>

      <Modal
        title="Disable two-factor authentication"
        open={disableOpen}
        onOk={() => disable2fa.mutate()}
        confirmLoading={disable2fa.isPending}
        okButtonProps={{ danger: true, disabled: password.length === 0 }}
        onCancel={() => {
          setDisableOpen(false);
          setPassword('');
        }}
      >
        <Typography.Paragraph>Enter your password to confirm.</Typography.Paragraph>
        <Input.Password
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Current password"
          onPressEnter={() => password && disable2fa.mutate()}
        />
      </Modal>
    </Space>
  );
}
